import {
  VisaDocumentModel,
  VisaDocumentMutationInput,
} from 'api-hooks/visa-document';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import Tabs, { TabsItemProps } from 'components/common/tabs';
import { Form, FormState } from 'components/elements';
import FormHeader from 'components/widgets/form-header';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import { FormContent } from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useForm } from 'react-hook-form';

import VisaDocumentFlightForm from './flight/visa-document-flight-form';
import VisaDocumentHotelForm from './hotel/visa-document-hotel-form';
import VisaDocumentActionComponent from './visa-document-action-component';
import {
  VisaDocumentFormSchema,
  VisaDocumentFormType,
  VisaDocumentMethodType,
} from './visa-document-form-type';
import VisaDocumentInformationForm from './visa-document-information-form';
import VisaDocumentTransactionList from './visa-document-transaction-list';
import useGetDefaultValues from '../hooks/use-get-default-values';

interface VisaDocumentFormProps {
  visaDocument?: VisaDocumentModel;
  onSubmit: (
    values: VisaDocumentMutationInput,
    form: VisaDocumentMethodType,
  ) => Promise<VisaDocumentModel | undefined>;
  renderError?: React.ReactNode;
}

export default function VisaDocumentForm(props: VisaDocumentFormProps) {
  const { renderError, visaDocument } = props;
  const { t } = useTranslation();
  const { asPath } = useRouter();

  const defaultValues = useGetDefaultValues(visaDocument);

  const visaDocumentFlights = visaDocument?.visaDocumentFlights;
  const visaDocumentHotels = visaDocument?.visaDocumentHotels;

  const resolver = useYupValidationResolver(VisaDocumentFormSchema());

  const methods = useForm({
    defaultValues,
    resolver,
  });

  const onSubmit = React.useCallback(
    async (values: VisaDocumentFormType) => {
      try {
        await props.onSubmit(values, methods);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const title = props.visaDocument
    ? [t('common:visa-document'), props.visaDocument.name].join(' - ')
    : t('common:add_extra', { extra: t('common:visa-document') });

  const [activeKey, setActiveKey] = React.useState<React.Key>('0');

  const render = React.useCallback(() => {
    switch (activeKey) {
      case '0':
        return <VisaDocumentInformationForm />;
      case '1':
        return (
          <VisaDocumentFlightForm visaDocumentFlights={visaDocumentFlights} />
        );
      case '2':
        return (
          <VisaDocumentHotelForm visaDocumentHotels={visaDocumentHotels} />
        );
      case '3':
        return <VisaDocumentTransactionList />;
      default:
        return <></>;
    }
  }, [activeKey, visaDocumentFlights, visaDocumentHotels]);

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!props.visaDocument}
      keyName={asPath}
    >
      <FormContent>
        <FormState>
          {({ editable }) => (
            <>
              <FormHeader
                data={props.visaDocument}
                noSubmit={!editable}
                noEdit={!editable}
                noDelete
                onRenderComponent={
                  props.visaDocument && (
                    <VisaDocumentActionComponent
                      visaDocument={props.visaDocument}
                    />
                  )
                }
                title={title}
              />
            </>
          )}
        </FormState>
        {renderError}

        <Tabs
          activeKey={activeKey}
          onChangeActive={({ activeKey }) => {
            setActiveKey(activeKey);
          }}
          items={
            [
              {
                title: t('common:detail'),
                component: <></>,
              },
              {
                title: t('common:flight'),
                component: <></>,
              },
              {
                title: t('common:hotel'),
                component: <></>,
              },
              visaDocument
                ? {
                    title: t('common:transaction_list'),
                    component: <></>,
                  }
                : null,
            ].filter(Boolean) as TabsItemProps[]
          }
          overrideStyles={{
            tabList: { minHeight: '0px' },
            tab: { minHeight: '0px', height: '0', flexGrow: '0' },
            tabPanel: { minHeight: '0px', height: '0', flexGrow: '0' },
          }}
        />
        <div>{render()}</div>
      </FormContent>
    </Form>
  );
}
